import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { downloadClientsApi } from "@/api/downloadClients";
import {
  grabsApi,
  type BulkRedownloadOutcome,
  type BulkRedownloadRequest,
} from "@/api/grabs";
import type { MediaKind } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Field, NumberInput, Select } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";

/**
 * Settings → Bulk redownload.
 *
 * Filter-driven redispatch of historical grabs through whichever
 * download client is currently enabled. The HTTP route is
 * `POST /api/v1/grabs/bulk-redownload` and the same logic backs the
 * `arr-server redownload` CLI subcommand.
 *
 * The recovery use case (homelab embedded-downloader truncation):
 *   1. Operator switches the embedded NZB client to disabled in
 *      Download Clients.
 *   2. They come here, set "Originally via" to the embedded client
 *      and "Media kind" to album, and run.
 *   3. Each historical grab gets re-dispatched to NZBGet (or whatever
 *      else is enabled now); the importer overwrites the partial
 *      files in place once the new download finishes.
 *
 * Long bulk runs block the request — a 500ms delay between
 * dispatches inside `bulk_redownload` keeps the indexer happy but
 * makes a 1000-row recovery sweep take ~8 minutes. The submit
 * button shows a spinner the whole time; refreshing the page
 * cancels nothing (the server keeps going).
 */
export function BulkRedownloadPage() {
  const { toast } = useToast();

  const clients = useQuery({
    queryKey: ["download-clients"],
    queryFn: downloadClientsApi.list,
  });

  const [mediaKind, setMediaKind] = useState<MediaKind | "">("");
  const [clientWas, setClientWas] = useState<string>("");
  const [status, setStatus] = useState<string>("imported");
  const [limit, setLimit] = useState<number>(50);
  const [outcome, setOutcome] = useState<BulkRedownloadOutcome | null>(null);

  const mut = useMutation({
    mutationFn: (req: BulkRedownloadRequest) => grabsApi.bulkRedownload(req),
    onSuccess: (o) => {
      setOutcome(o);
      const summary = `${o.dispatched} dispatched, ${o.skipped} skipped, ${o.failed} failed`;
      if (o.failed > 0) {
        toast(`Bulk redownload completed with errors: ${summary}`, "error");
      } else if (o.matched === 0) {
        toast("No matching grabs.", "info");
      } else {
        toast(`Bulk redownload complete: ${summary}`, "success");
      }
    },
    onError: (e) =>
      toast(
        e instanceof Error ? e.message : "Bulk redownload failed",
        "error",
      ),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bulk redownload"
        description="Re-dispatch historical grabs through whichever download client is currently enabled. Built for the embedded-downloader truncation recovery — switch to NZBGet, disable embedded, then replay your library here."
      />

      <div className="space-y-4 rounded border border-line bg-surface/40 p-5">
        <p className="text-xs leading-relaxed text-foreground/70">
          Heads up: each row replays through{" "}
          <code className="font-mono">dispatch_chosen_release</code>, which
          walks <em>currently-enabled</em> download clients in priority order.
          Disable the embedded NZB client in{" "}
          <strong>Settings → Download Clients</strong> first, otherwise the
          replay will route right back to the broken downloader.
        </p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="Media kind"
            hint="Restrict to one media type, or leave blank for all."
          >
            <Select
              value={mediaKind}
              onChange={(e) =>
                setMediaKind((e.target.value as MediaKind | "") ?? "")
              }
            >
              <option value="">All</option>
              <option value="movie">Movies</option>
              <option value="episode">Episodes</option>
              <option value="album">Albums</option>
            </Select>
          </Field>

          <Field
            label="Originally dispatched via"
            hint="Only replay grabs that originally went to this download client. The recovery flow points this at the embedded client."
          >
            <Select
              value={clientWas}
              onChange={(e) => setClientWas(e.target.value)}
              disabled={clients.isLoading}
            >
              <option value="">Any client</option>
              {(clients.data ?? []).map((c) => (
                <option key={c.id} value={String(c.id)}>
                  #{c.id} {c.name} ({c.kind}
                  {c.enabled ? "" : ", disabled"})
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Status"
            hint="Default `imported` — replay things we thought we'd finished. Switch to `failed` to retry truncation-guard rejections."
          >
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="imported">imported</option>
              <option value="completed">completed</option>
              <option value="failed">failed</option>
              <option value="queued">queued</option>
              <option value="downloading">downloading</option>
              <option value="removed">removed</option>
            </Select>
          </Field>

          <Field
            label="Limit"
            hint="Cap on how many grabs to replay in this run. The server inserts a 500ms gap between dispatches, so high limits take time."
          >
            <NumberInput
              min={1}
              max={1000}
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value) || 1)}
            />
          </Field>
        </div>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            loading={mut.isPending}
            onClick={() => {
              const req: BulkRedownloadRequest = {
                status,
                limit,
              };
              if (mediaKind) req.media_kind = mediaKind;
              if (clientWas) req.client_was = Number(clientWas);
              setOutcome(null);
              mut.mutate(req);
            }}
          >
            Run bulk redownload
          </Button>
          <span className="text-xs text-foreground/55">
            No dry-run mode here — for a preview, use{" "}
            <code className="font-mono">arr-server redownload --dry-run</code>{" "}
            from the host.
          </span>
        </div>
      </div>

      {outcome && <OutcomePanel outcome={outcome} />}
    </div>
  );
}

function OutcomePanel({ outcome }: { outcome: BulkRedownloadOutcome }) {
  return (
    <section className="space-y-3 rounded border border-line bg-surface/40 p-5">
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <Stat label="Matched" value={outcome.matched} />
        <Stat label="Dispatched" value={outcome.dispatched} tone="good" />
        <Stat label="Skipped" value={outcome.skipped} tone="neutral" />
        <Stat label="Failed" value={outcome.failed} tone="bad" />
      </div>

      {outcome.details.length > 0 && (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full text-xs">
            <thead className="bg-surface-2/50 text-left text-[10px] uppercase tracking-[0.12em] text-foreground/55">
              <tr>
                <th className="px-3 py-2 font-medium">Grab id</th>
                <th className="px-3 py-2 font-medium">Release</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">New grab</th>
                <th className="px-3 py-2 font-medium">Note</th>
              </tr>
            </thead>
            <tbody>
              {outcome.details.map((d) => (
                <tr key={d.grab_id} className="border-t border-line">
                  <td className="px-3 py-2 font-mono">#{d.grab_id}</td>
                  <td className="max-w-md truncate px-3 py-2 font-mono">
                    {d.release_title}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        d.status === "ok"
                          ? "text-good"
                          : d.status === "skipped"
                            ? "text-foreground/60"
                            : "text-bad"
                      }
                    >
                      {d.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-foreground/70">
                    {d.new_grab_id != null ? `#${d.new_grab_id}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-foreground/60">
                    {d.message ?? ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "good" | "bad" | "neutral";
}) {
  const colour =
    tone === "good"
      ? "text-good"
      : tone === "bad"
        ? "text-bad"
        : tone === "neutral"
          ? "text-foreground/70"
          : "text-foreground";
  return (
    <div className="space-y-0.5">
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-foreground/40">
        {label}
      </div>
      <div className={`text-lg font-semibold tabular-nums ${colour}`}>
        {value}
      </div>
    </div>
  );
}
